// Turn a decoded composite-ss uv tile into wind-barb point features for a
// MapLibre symbol layer (reusing Skysight's barb sprite, like LaunchSitesLayer).
// The heavy lifting (fetch tile + canvas getImageData) is done in WindBarbsLayer;
// the pure tile→lnglat math and feature gridding live here so they can be tested.

import { decodeUV, windSpeedDir, msToKt } from './decode';

// Web-mercator inverse: pixel (px,py) within tile (x,y) at zoom z → [lng, lat].
// px/py may be fractional; tileSize is the raster's pixel width/height.
export function tilePixelToLngLat(z, x, y, px, py, tileSize) {
  const n = Math.pow(2, z);
  const fx = x + px / tileSize;
  const fy = y + py / tileSize;
  const lng = (fx / n) * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * fy) / n))) * 180) / Math.PI;
  return [lng, lat];
}

// Sprite barb icons are named by knots, rounded to the nearest 5, clamped 0..100.
const spriteSpeedName = (knots) => String(Math.min(100, Math.max(0, Math.round(knots / 5) * 5)));

// Decode the barb at one pixel (px,py) of a tile's RGBA `data`.
// Returns { wspd, wdir, speedMs } or null (sentinel / no data / out of bounds).
export function barbAtPixel(data, width, height, px, py, range) {
  if (px < 0 || py < 0 || px >= width || py >= height) return null;
  const i = (py * width + px) * 4;
  const uv = decodeUV(
    data[i] / 255, data[i + 1] / 255, data[i + 2] / 255, data[i + 3] / 255,
    range.min, range.max,
  );
  if (!uv) return null;
  const { speedMs, dirFrom } = windSpeedDir(uv);
  return { wspd: spriteSpeedName(msToKt(speedMs)), wdir: Math.round(dirFrom), speedMs };
}

// data: RGBA Uint8(Clamped)Array of length width*height*4 (0..255 per channel).
// range: { min:[u,v], max:[u,v] } in m/s. step: sample every `step` pixels.
export function barbFeaturesFromTile({ data, width, height, z, x, y, tileSize, range, step = 1 }) {
  const features = [];
  for (let py = 0; py < height; py += step) {
    for (let px = 0; px < width; px += step) {
      const barb = barbAtPixel(data, width, height, px, py, range);
      if (!barb) continue; // sentinel / no data
      const [lng, lat] = tilePixelToLngLat(z, x, y, px + 0.5, py + 0.5, tileSize);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { wspd: barb.wspd, wdir: barb.wdir, speedMs: barb.speedMs },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}
